import mixins from 'vue-typed-mixins';
import _ from 'lodash';

import { CourseProgress } from '@/openapi/user';

import CourseProgressMixin from '@/user/mixins/CourseProgressMixin';
import RoutesMixin from '@/user/mixins/RoutesMixin';
import StudyMixin from '@/user/mixins/StudyMixin';
import UserMixin from '@/user/mixins/UserMixin';

import ConfirmDialog from '@/user/components/dialog/confirm/ConfirmDialog.vue';
import Header from '@/user/components/header/Header.vue';
import BackButton from '@/user/components/header/backButton/BackButton.vue';
import RestErrorBar from '@/user/components/study/restErrorBar/RestErrorBar.vue';
import RestTime from '@/user/components/study/restTime/RestTime.vue';
import StudyResultDialog from '@/user/components/study/dialog/result/StudyResultDialog.vue';
import { NumberChoiceType, NumberUnit, NUMBER_UNITS } from '@/user/pages/authorized/study/number/NumberDefinition';


const INCORRECT_LIMIT = 3;
const MATRIX_COLUMN_COUNT = 10;

interface MatrixCell {
  no: number;
  display: boolean;
};

export default mixins(
  CourseProgressMixin,
  RoutesMixin,
  StudyMixin,
  UserMixin,
).extend({

  name: 'Number',

  components: {
    Header,
    BackButton,
    RestErrorBar,
    RestTime,
    ConfirmDialog,
    StudyResultDialog,
  },

  data(): {
    visibleBackDialog: boolean;
    matrixCells: MatrixCell[][];
  } {
    return {
      visibleBackDialog: false,
      matrixCells: [],
    };
  },

  computed: {
    unit(): NumberUnit {
      return NUMBER_UNITS.find(u => u.sectionNo === this.studySectionNo && u.unitNo === this.studyUnitNo)!;
    },
    isPointQuestion(): boolean {
      return this.unit.choiceType === NumberChoiceType.numbers;
    },
    isChoiceNumbers(): boolean {
      return this.unit.choiceType === NumberChoiceType.numbers;
    },
    isChoicePoints(): boolean {
      return this.unit.choiceType === NumberChoiceType.points;
    },
    isChoiceMatrix(): boolean {
      return this.unit.choiceType === NumberChoiceType.matrixFind
          || this.unit.choiceType === NumberChoiceType.matrixBlank
          || this.unit.choiceType === NumberChoiceType.matrixFill;
    },
    isMatrixLineUp(): boolean {
      return this.unit.choiceType === NumberChoiceType.matrixBlank
          || this.unit.choiceType === NumberChoiceType.matrixFill;
    },
    // StudyMixinを上書き、返却型も変更
    questions(): number[] {
      return this.$_createQuestionList(this.studySectionNo, this.studyUnitNo);
    },
    // StudyMixinを上書き、返却型も変更
    question(): number | null {
      return this.questionIndex === -1 ? null : this.questions[this.questionIndex];
    },
  },

  async created(): Promise<void> {
    // font読み込み
    await this.loadFont();

    // 初期化
    this.$_initialize();
  },

  destroyed(): void {
    this.clearRestTimeInterval();
    this.clearStudyVariable();
    this.clearStudyResult();
  },

  methods: {

    back(): void {
      this.visibleBackDialog = false;
      this.$router.push(this.routes.UnitSelect);
    },

    // StudyMixinを上書き
    nextQuestion(): void {
      this.forwardQuestionIndex();
      if (this.is1stQuestion) {
        if (this.isChoiceMatrix) {
          this.matrixCells = [];
          const rowCount = this.unit.choiceCount / MATRIX_COLUMN_COUNT;  // 割り切れるのは前提
          let no = 0;
          for (let row = 0; row < rowCount; row++) {
            const columns: MatrixCell[] = [];
            for (let col = 0; col < MATRIX_COLUMN_COUNT; col++) {
              no++;
              columns.push({
                no: no,
                display: this.unit.choiceType !== NumberChoiceType.matrixFill,
              });
            }
            this.matrixCells.push(columns);
          }
        }
        // タイマー設定
        this.startTimer(this.unit.stndardSec);
      }
    },

    choose(choice: number): void {
      // 答え合わせ
      const isCorrect = this.question === choice;

      // 誤答の学習メモを保存、不正解回数の更新
      if (!isCorrect) {
        this.incrementIncorretCount();
        this.addStudyMemo(String(this.question)); // 間違えた数字を保持

      // すうじならべで正解なら表示制御
      } else if (this.isMatrixLineUp) {
        const index = choice - 1;
        const rowIndex = Math.floor(index / MATRIX_COLUMN_COUNT);
        const colIndex = Math.floor(index % MATRIX_COLUMN_COUNT);
        this.matrixCells[rowIndex][colIndex].display = this.unit.choiceType === NumberChoiceType.matrixFill;
      }

      this.judgeScoreResult(false, isCorrect, INCORRECT_LIMIT);
    },

    // StudyMixinを上書き、学習結果保存処理用
    isCourseComplete(isCorrect: boolean): boolean {
      // 不正解の場合はfalse
      if (!isCorrect) return false;

      // 回答中のユニット以外の全単元、ユニットが終了しているか
      for (const section of this.sections) {
        const progress  = this.progresses.find(s => s.sectionNo === section.sectionNo);
        // 単元内のユニット数（回答中の単元の場合はユニット数を減らしておく）
        const unitCount = section.sectionNo === this.studySectionNo ? section.unitCount - 1 : section.unitCount;
        // 単元の進捗が存在しない、もしくは単元内のユニット数より少ない場合はfalse
        if (progress === undefined || progress.unitNos.length < unitCount) return false;
      }

      return true;
    },

    // StudyMixinを上書き、学習結果保存処理用
    updateProgress(isCorrect: boolean): void {
      // 不正解の場合はreturn
      if (!isCorrect) return;

      // 進捗に学習中のユニットがない場合は追加
      const progress = this.getProgress();
      let   unitNos  = progress.unitNos;
      if (!unitNos.includes(this.studyUnitNo)) {
        unitNos.push(this.studyUnitNo);
        unitNos = _.sortBy(progress.unitNos);
      }

      // 進捗に学習中の単元がない場合は追加
      let progresses = this.progresses;
      if (!progresses.some(p => p.sectionNo === this.studySectionNo)) {
        progresses.push(progress);
        progresses = _.sortBy(progresses, p => p.sectionNo);
        this.setProgresses(progresses);
      }
    },

    // StudyMixinを上書き、学習結果保存処理用
    getProgress(): CourseProgress {
      const progress = this.progresses.find(p => p.sectionNo === this.studySectionNo);
      return progress ?? { sectionNo: this.studySectionNo, unitNos: [] };
    },

    // StudyMixinを上書き、学習終了後に学習続行用
    initializeCourseComponent(): void {
      this.initializeStudyVariable();
      this.$_initialize();
    },

    async $_initialize(): Promise<void> {
      // 進捗、セクション読込
      await this.loadProgressesState(this.studyCourseId);

      // 初期化
      this.initializeStudyVariable();
      this.initializeStudyRequest();
      this.visibleBackDialog = false;

      // 回答可能回数
      this.setIncorretLimit(INCORRECT_LIMIT);

      // ステージ名設定
      const stageName = this.$_createStageName();
      this.setStageName(stageName);

      // 問題開始
      this.initializeQuestionIndex();
      this.nextQuestion();
    },

    /**
     * 問題リストを作成します。
     *
     * @param sectionNo 単元No
     * @param unitNo ユニットNo
     * @return 問題配列
     */
    $_createQuestionList(sectionNo: number, unitNo: number) {

      // 単元1：すうじ10
      if (sectionNo === 1) {
        switch (unitNo) {
          case 1:
          case 4:
            return this.$_createQuestion1_1(0);
          case 2:
          case 5:
            return this.$_createQuestion1_1(5);
          case 3:
          case 6:
            const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
            this.$_shuffle(nums);
            return nums;
        }
      }
      // 単元2：すうじ20
      else if (sectionNo === 2) {
        switch (unitNo) {
          case 1:
          case 5:
            return this.$_createQuestion1_1(10);
          case 2:
          case 6:
            const nums2 = this.$_createQuestion2_2(2);
            this.$_shuffle(nums2);
            return nums2;
          case 3:
          case 7:
            return this.$_createQuestion1_1(15);
          case 4:
          case 8:
            const nums3 = this.$_createQuestion2_2(3);
            this.$_shuffle(nums3);
            return nums3;
        }
      }
      // 単元3：すうじ30
      else if (sectionNo === 3) {
        switch (unitNo) {
          case 1:
          case 5:
            return this.$_createQuestion1_1(20);
          case 2:
          case 6:
            const nums2 = this.$_createQuestion2_2(4);
            this.$_shuffle(nums2);
            return nums2;
          case 3:
          case 7:
            return this.$_createQuestion1_1(25);
          case 4:
          case 8:
            const nums3 = this.$_createQuestion2_2(5);
            this.$_shuffle(nums3);
            return nums3;
        }
      }
      // 単元4：すうじさがし
      else if (sectionNo === 4) {
        const nums1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        this.$_shuffle(nums1);
        switch (unitNo) {
          case 1:
            const nums2_1 = [1, 1, 1, 2, 2, 2, 3, 3, 3, 3];
            this.$_shuffle(nums2_1);
            return this.$_join(nums1, nums2_1);
          case 2:
            const nums2_2 = [1, 1, 2, 2, 3, 3, 3, 4, 4, 4];
            this.$_shuffle(nums2_2);
            return this.$_join(nums1, nums2_2);
          case 3:
            const nums2_3 = [1, 2, 2, 3, 3, 4, 4, 5, 5, 5];
            this.$_shuffle(nums2_3);
            return this.$_join(nums1, nums2_3);
          case 4:
            const nums2_4 = [1, 2, 3, 3, 4, 5, 5, 6, 6, 6];
            this.$_shuffle(nums2_4);
            return this.$_join(nums1, nums2_4);
          case 5:
            const nums2_5 = [1, 2, 3, 4, 5, 6, 6, 7, 7, 7];
            this.$_shuffle(nums2_5);
            return this.$_join(nums1, nums2_5);
          case 6:
            const nums2_6 = [2, 3, 4, 5, 6, 7, 7, 8, 8, 8];
            this.$_shuffle(nums2_6);
            return this.$_join(nums1, nums2_6);
          case 7:
            const nums2_7 = [1, 3, 5, 6, 7, 8, 8, 9, 9, 9];
            this.$_shuffle(nums2_7);
            return this.$_join(nums1, nums2_7);
          case 8:
            const nums2_8 = [2, 4, 6, 7, 8, 9, 9,10,10,10];
            this.$_shuffle(nums2_8);
            return this.$_join(nums1, nums2_8);
          case 9:
            const nums2_9 = [1, 3, 5, 7, 9,10,10,11,11,11];
            this.$_shuffle(nums2_9);
            return this.$_join(nums1, nums2_9);
          case 10:
            const nums2_10 = [2, 4, 6, 8,10,11,11,12,12,12];
            this.$_shuffle(nums2_10);
            return this.$_join(nums1, nums2_10);
        }
      }
      // 単元5：すうじならべ表
      else if (sectionNo === 5) {
        switch (unitNo) {
          case 1:  return this.$_createQuestion5_1(3);
          case 2:  return this.$_createQuestion5_1(4);
          case 3:  return this.$_createQuestion5_1(5);
          case 4:  return this.$_createQuestion5_1(6);
          case 5:  return this.$_createQuestion5_1(7);
          case 6:  return this.$_createQuestion5_1(8);
          case 7:  return this.$_createQuestion5_1(9);
          case 8:  return this.$_createQuestion5_1(10);
          case 9:  return this.$_createQuestion5_1(11);
          case 10: return this.$_createQuestion5_1(12);
        }
      }
      // 単元6：すうじならべ裏
      else if (sectionNo === 6) {
        switch (unitNo) {
          case 1:  return this.$_createQuestion5_1(3);
          case 2:  return this.$_createQuestion5_1(4);
          case 3:  return this.$_createQuestion5_1(5);
          case 4:  return this.$_createQuestion5_1(6);
          case 5:  return this.$_createQuestion5_1(7);
          case 6:  return this.$_createQuestion5_1(8);
          case 7:  return this.$_createQuestion5_1(9);
          case 8:  return this.$_createQuestion5_1(10);
          case 9:  return this.$_createQuestion5_1(11);
          case 10: return this.$_createQuestion5_1(12);
        }
      }
      // 単元7：すうじならべ上級
      else if (sectionNo === 7) {
        return this.$_createQuestion5_1(12);
      }

      return new Array(10).fill(0);
    },

    /**
     * 単元1、ユニット1の形式の問題を作成します。
     *
     * @param add 加算数字
     * @return 問題配列
     */
    $_createQuestion1_1(add: number): number[] {
      const ret = [1, 2, 3, 4, 5, 0, 0, 0, 0, 0];

      let str = '';

      // まずは2から4の中から1つを選ぶ
      str += String(Math.floor(Math.random() * 100) % 3 + 2);

      // 5桁になるまで繰り返す
      while (str.length < 5) {
        const tmp = String(Math.floor(Math.random() * 100) % 5 + 1);
        if (str.indexOf(tmp) == -1) str += tmp;
      }

      // 配列に直す
      for (let i = 0; i < 5; i++) {
        ret[i+5] = Number(str.substring(i, i+1));
      }

      // 全体を加算
      for (let i = 0; i < 10; i++) ret[i] = ret[i] + add;

      return ret;
    },

    /**
     * 単元2、ユニット2の形式の問題（シャッフル前）を作成します。
     *
     * @param step 2 or 3 or 4 or 5
     * @return 問題配列
     */
    $_createQuestion2_2(step: number): number[] {

      const nums = [0, 0, 0, 0, 0, step*5+1, step*5+2, step*5+3, step*5+4, step*5+5];
      nums[0] = Math.floor(Math.random() * 100) % step + 1;
      nums[1] = Math.floor(Math.random() * 100) % step + step + 1;
      nums[2] = Math.floor(Math.random() * 100) % step + step * 2 + 1;
      nums[3] = Math.floor(Math.random() * 100) % step + step * 3 + 1;
      nums[4] = Math.floor(Math.random() * 100) % step + step * 4 + 1;

      return nums;
    },

    /**
     * 単元5、ユニット1の形式の問題を作成します。
     *
     * @param 行数（3から12）
     * @return 問題配列
     */
    $_createQuestion5_1(row: number): number[] {

      // 初期値の作成
      const nums = new Array(row*10);
      const nums1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      const nums2 = new Array(row);
      for (let i = 0; i < row; i++) nums2[i] = i;
      this.$_shuffle(nums1);
      this.$_shuffle(nums2);
      for (let i = 0; i < row; i++) {
        for (let j = 0; j < 10; j++) {
          nums[i*10+j] = nums2[i] * 10 + nums1[j];
        }
      }

      for (let i = 1; i < row*10; i++) {
        let count = 0;
        while (true) {
          if (count > 10) break;

          const tmp0 = nums[i-1];
          const tmp1 = nums[i];

          // 1の位の距離
          let x0 = tmp0 % 10;
          let x1 = tmp1 % 10;
          if (x0 == 0) x0 = 10;
          if (x1 == 0) x1 = 10;
          let dx = x0 - x1;
          if (dx < 0) dx = -dx;

          // 10の位の距離
          const y0 = (tmp0 - x0) / 10;
          const y1 = (tmp1 - x1) / 10;
          let dy = y0 - y1;
          if (dy < 0) dy = -dy;

          // マスの距離
          if (dx + dy >= 5) break;

          // 値の入れ替え
          const index = i + Math.floor(Math.random() * 10000) % (row*10 - i);
          //console.warn("i=" + i + ", nums[i-1]=" + nums[i-1] + ", nums[i]=" + nums[i] + ", index=" + index + ", dx=" + dx + ", dy=" + dy);
          const tmp = nums[i];
          nums[i] = nums[index];
          nums[index] = tmp;
          count++;
        }
      }

      return nums;
    },

    /**
     * 配列の数値をシャッフルします。
     *
     * @param nums 問題配列
     */
    $_shuffle(nums: number[]): void {

      // 次の2つの数は先頭に来ないようにする
      let start = nums[0];
      let end   = nums[nums.length-1];

      let count = 0;
      while (true) {
        const index1 = Math.floor(Math.random() * 100) % nums.length;
        const index2 = Math.floor(Math.random() * 100) % nums.length;
        if (index1 == index2) continue;

        // 値の入れ替え
        const tmp = nums[index1];
        nums[index1] = nums[index2];
        nums[index2] = tmp;

        count++;
        if (count < 10) continue;

        // 先頭の値のチェック
        if (nums[0] == start) continue;
        if (nums[0] == end) continue;

        // 隣どうしの値のチェック
        let okFlag = true;
        for (let i = 0; i < nums.length-1; i++) {
          if (nums[i] == nums[i+1]) {
            okFlag = false;
            break;
          }
        }
        if (okFlag) break;
      }
    },

    /**
     * 1の桁と10の桁を合わせます。
     *
     * @param nums1 1の桁の位置（1-10）
     * @param nums2 10の桁の位置（1-12）
     * @return 問題配列
     */
    $_join(nums1: number[], nums2: number[]): number[] {
      const ret = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      for (let i = 0; i < 10; i++) ret[i] = (nums2[i]-1)*10 + nums1[i];
      return ret;
    },

    /**
     * ステージ名を生成します。
     *
     * @return ステージ名
     */
    $_createStageName(): string {
      return `${this.getSection(this.studySectionNo).sectionName} (${this.studyUnitNo})`;
    }

  },

});
